/**
 * GuidesPage — Apartado propio de Guías de Despacho (DTE 52).
 *
 * Las guías no se contabilizan (solo mueven inventario), así que viven aquí en
 * vez de mezclarse con Documentos de Venta / Compras. Dos pestañas:
 *   - Emitidas: SaleDocument document_type='GD'. Se pueden seleccionar y
 *     "facturar" (consolidar en una factura que las referencia — Fase 4).
 *   - Recibidas: InboundDTE tipo_dte=52 (solo consulta).
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { formatMoneyForTenant } from '@/lib/locale'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Truck, FileText, Loader2, Plus, Eye, Send } from 'lucide-react'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage, extractFieldErrors } from '@/lib/errorUtils'
import { useSaleDocuments, useInvoiceFromGuides, useEmitDocument } from '@/modules/sales/hooks/useSales'
import { useDTETypes } from '@/modules/config/hooks/useConfig'
import { useInboundDTEs } from '@/modules/dte-inbound/hooks/useInboundDTE'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'

const clp = (v?: string | number | null) =>
  v == null || v === '' ? '—'
    : formatMoneyForTenant(Number(v))

function IssuedGuides() {
  const locale = useTenantLocale()
  const navigate = useNavigate()
  const toast = useToast()
  const { can } = usePermission()
  const canInvoice = can('sales.documents', 'create')
  const canCreate = can('sales.documents', 'create')
  const canEmit = can('sales.documents', 'edit')
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR: esta lista la pagina el backend (`?ordering=`).
  const [orden, setOrden] = useState<SortState | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const { data, isLoading } = useSaleDocuments({ type: 'GD', page, page_size: 25, ordering: sortStateToParam(orden) })
  const invoice = useInvoiceFromGuides()
  const emit = useEmitDocument()
  // La Guía de Despacho (DTE 52) debe estar habilitada en Configuración → Tipos de
  // documento para poder generarla. Optimista mientras carga (la mayoría la tiene).
  const { data: dteTypes } = useDTETypes()
  const gdEnabled = dteTypes ? dteTypes.some(t => t.is_active && t.internal_code === 'GD') : true

  // Persiste los filtros de guías para que el rail del 360 muestre SOLO guías
  // (el listado de documentos normal las excluye).
  useEffect(() => {
    sessionStorage.setItem('sales-guides:rail-params', JSON.stringify({ ordering: '-issue_date' }))
  }, [])

  const rows = data?.documents ?? []
  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const view = (id: string) => navigate(`/sales/guides/${id}/360`)

  const handleEmit = (id: string) => {
    emit.mutate(id, {
      onSuccess: () => toast.success('Guía enviada a emisión al SII.'),
      onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo emitir la guía.')),
    })
  }

  const handleInvoice = () => {
    invoice.mutate(
      { guide_ids: [...selected] },
      {
        onSuccess: (doc) => {
          toast.success('Factura borrador creada desde las guías seleccionadas.')
          setSelected(new Set())
          navigate(`/sales/documents/${doc.id}/360`)
        },
        onError: (e) => {
          const fields = extractFieldErrors(e)
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          toast.error(err.response?.data?.error?.message ?? (fields ? 'No se pudo facturar.' : 'No se pudo facturar.'))
        },
      },
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="text-sm text-content-muted">
          {canInvoice && selected.size > 0
            ? `${selected.size} guía(s) seleccionada(s)`
            : 'Genera, revisa y emite tus guías de despacho desde aquí.'}
        </p>
        <div className="flex items-center gap-2">
          {canInvoice && selected.size > 0 && (
            <button
              onClick={handleInvoice}
              disabled={invoice.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 border border-divider text-content text-sm font-medium rounded-lg hover:bg-soft disabled:opacity-50"
            >
              {invoice.isPending ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
              Facturar seleccionadas
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => navigate('/sales/documents/new?type=GD')}
              disabled={!gdEnabled}
              title={gdEnabled
                ? 'Crear una nueva guía de despacho'
                : "Habilita 'Guía de Despacho' en Configuración → Tipos de documento para poder crearlas."}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
            >
              <Plus size={15} /> Nueva Guía
            </button>
          )}
        </div>
      </div>

      <DataTable<typeof rows[number]>
        {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
        columns={[
          // 🟢 El checkbox es una columna como cualquier otra: acá la selección
          // es SOLO por fila, sin «seleccionar todos» en el encabezado, que es
          // lo único que el componente no modela.
          ...(canInvoice ? [{
            key: 'seleccion', header: '', width: 40,
            render: (g: typeof rows[number]) => (
              <input type="checkbox" checked={selected.has(g.id)} onChange={() => toggle(g.id)}
                aria-label={`Seleccionar guía ${g.folio || 'borrador'}`}
                className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary" />
            ),
          }] : []),
          { key: 'folio', header: 'Folio', sortable: true,
            sortValue: g => Number(g.folio) || 0,
            render: g => (
              <button onClick={() => view(g.id)}
                className="font-medium text-content hover:text-primary hover:underline">
                {g.folio || 'Borrador'}
              </button>
            ) },
          { key: 'issue_date', header: 'Fecha', sortable: true,
            render: g => <span className="text-content-muted">{g.issue_date}</span> },
          { key: 'customer_name', header: 'Cliente', sortable: true,
            render: g => <span className="text-content">{g.customer_name}</span> },
          { key: 'total', header: 'Total', numeric: true, sortable: true,
            sortValue: g => Number(g.total),
            render: g => <span className="text-content">{clp(g.total)}</span> },
          { key: 'status_display', header: 'Estado',
            render: g => <span className="text-content-muted">{g.status_display}</span> },
          { key: 'acciones', header: 'Acciones', width: 190,
            render: g => (
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => view(g.id)} title="Ver"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-divider hover:bg-soft text-content-muted">
                  <Eye size={14} /> Ver
                </button>
                {canEmit && (g.status === 'draft' || g.status === 'rejected') && (
                  <button onClick={() => handleEmit(g.id)} disabled={emit.isPending}
                    title={g.status === 'rejected' ? 'Reintentar emisión' : 'Emitir al SII'}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-50">
                    <Send size={14} /> {g.status === 'rejected' ? 'Reintentar' : 'Emitir'}
                  </button>
                )}
              </div>
            ) },
        ]}
        rows={rows}
        rowKey={g => g.id}
        minWidth={900}
        loading={isLoading}
        empty={<EmptyState title="No hay guías de despacho emitidas." size="sm" />}
        caption="Guías de despacho emitidas"
      />

      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data.count}
          onPageChange={setPage}
          locale={locale}
          className="mt-3"
        />
      )}
    </div>
  )
}

function ReceivedGuides() {
  const locale = useTenantLocale()
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR: esta lista la pagina el backend (`?ordering=`).
  const [orden, setOrden] = useState<SortState | null>(null)
  const { data, isLoading } = useInboundDTEs({ tipo_dte: 52, page, page_size: 25, ...(orden ? { ordering: sortStateToParam(orden)! } : {}) })
  const rows = data?.items ?? []

  return (
    <>
      <DataTable<typeof rows[number]>
        {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
        columns={[
          { key: 'folio', header: 'Folio', sortable: true,
            sortValue: d => Number(d.folio) || 0,
            render: d => <span className="font-medium text-content">{d.folio ?? '—'}</span> },
          { key: 'fecha_emision', header: 'Fecha', sortable: true,
            render: d => <span className="text-content-muted">{d.fecha_emision ?? '—'}</span> },
          { key: 'razon_social_emisor', header: 'Proveedor', sortable: true,
            render: d => <span className="text-content">{d.razon_social_emisor}</span> },
          { key: 'monto_total', header: 'Total', numeric: true, sortable: true,
            sortValue: d => Number(d.monto_total),
            render: d => <span className="text-content">{clp(d.monto_total)}</span> },
          { key: 'status_display', header: 'Estado',
            render: d => <span className="text-content-muted">{d.status_display}</span> },
        ]}
        rows={rows}
        rowKey={d => d.id}
        minWidth={720}
        loading={isLoading}
        empty={<EmptyState title="No hay guías de despacho recibidas." size="sm" />}
        caption="Guías de despacho recibidas de proveedores"
      />
      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data.count}
          onPageChange={setPage}
          locale={locale}
        />
      )}
    </>
  )
}

export function GuidesPage() {
  const [tab, setTab] = useState<'issued' | 'received'>('issued')

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Guías de Despacho"
        subtitle="Traslado de mercadería (DTE 52). No reconocen ingreso: solo mueven inventario."
        help={HELP['sales.guides']}
      />

      <div className="flex gap-1 border-b border-divider mb-4">
        {([['issued', 'Emitidas', Truck], ['received', 'Recibidas', FileText]] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? 'border-primary text-primary' : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      {tab === 'issued' ? <IssuedGuides /> : <ReceivedGuides />}
    </div>
  )
}

export default GuidesPage
